import Icon from '../ui/Icon';
import BrandLogo from '../ui/BrandLogo';
import RowActionsMenu from '../users/RowActionsMenu';
import { PackageStatusBadge } from './PackageStatus';
import { ACTIVE_LOCK_HINT, describeTime, getLevels, getTier, levelCount, levelStyle, packageTitle } from './couponAdapter';

// Actions stay visible at the right edge when the level columns scroll sideways
const STICKY_ACTIONS = 'sticky right-0 z-10 shadow-[-1px_0_0_0_var(--color-surface-container)]';

const TH = 'py-3 px-4 text-left text-[11px] leading-[14px] font-semibold text-on-surface-variant uppercase tracking-wider whitespace-nowrap';

const daysLabel = (days) => `Valid ${days} ${days === 1 ? 'day' : 'days'}`;

function LevelCell({ level }) {
  const style = levelStyle(level.level);
  if (level.missing) {
    return (
      <td className="py-3.5 px-4 align-top">
        <span className="text-xs italic text-on-surface-variant">Not set</span>
      </td>
    );
  }
  return (
    <td className="py-3.5 px-4 align-top">
      <div className="flex items-start gap-2.5 min-w-[190px]">
        <BrandLogo url={level.logoUrl} brand={level.brandName} size="sm" />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[13px] font-bold text-on-surface truncate max-w-[130px]" title={level.brandName}>
              {level.brandName}
            </span>
            {level.scansRequired != null && (
              <span className={`px-1.5 rounded text-[10px] leading-4 font-data-mono font-semibold whitespace-nowrap ${style.chip}`}>
                {level.scansRequired} Scans
              </span>
            )}
          </div>
          <span className="text-xs text-on-surface-variant mt-0.5 line-clamp-2 break-words" title={level.offerText}>
            {level.offerText}
          </span>
          {(level.couponCode || level.validForDays) && (
            <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-on-surface-variant">
              {level.couponCode && (
                <span>
                  Code: <span className="font-data-mono font-semibold text-on-surface">{level.couponCode}</span>
                </span>
              )}
              {level.validForDays && <span className="tabular">{daysLabel(level.validForDays)}</span>}
            </span>
          )}
        </div>
      </div>
    </td>
  );
}

function IconButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
    >
      <Icon name={icon} size={20} />
    </button>
  );
}

function SkeletonRows({ columns, count = 3 }) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={i} className="animate-pulse" aria-hidden="true">
      <td className="py-4 px-4">
        <div className="h-3 w-28 bg-surface-container rounded mb-1.5" />
        <div className="h-2.5 w-10 bg-surface-container-low rounded" />
      </td>
      <td className="py-4 px-4">
        <div className="h-5 w-16 bg-surface-container rounded-full" />
      </td>
      <td className="py-4 px-4">
        <div className="h-3 w-20 bg-surface-container rounded mb-1.5" />
        <div className="h-2.5 w-14 bg-surface-container-low rounded" />
      </td>
      {Array.from({ length: columns }, (_, j) => (
        <td key={j} className="py-4 px-4">
          <div className="flex gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-surface-container" />
            <div className="space-y-1.5">
              <div className="h-3 w-24 bg-surface-container rounded" />
              <div className="h-2.5 w-32 bg-surface-container-low rounded" />
            </div>
          </div>
        </td>
      ))}
      <td className={`py-4 px-4 ${STICKY_ACTIONS} bg-surface-container-lowest`}>
        <div className="h-6 w-20 ml-auto bg-surface-container-low rounded" />
      </td>
    </tr>
  ));
}

/** "Ledger Table": one row per package, one column per reward tier. */
export default function CouponsTable({ coupons, tiers, loading, refreshing, onView, onEdit, onMakeActive, onDuplicate, onDelete }) {
  const columns = Math.max(levelCount(tiers), ...coupons.map((c) => levelCount([], c)), loading ? 4 : 0);

  return (
    <div className="w-full overflow-x-auto" aria-busy={loading || refreshing}>
      <table className="w-full min-w-[1180px] text-left border-collapse">
        <caption className="sr-only">Coupon packages</caption>
        <thead>
          <tr className="bg-surface-container-low border-b border-surface-container">
            <th scope="col" className={`${TH} min-w-[170px]`}>
              Package
            </th>
            <th scope="col" className={TH}>
              Status
            </th>
            <th scope="col" className={`${TH} min-w-[130px]`}>
              Last Updated
            </th>
            {Array.from({ length: columns }, (_, i) => {
              const tier = getTier(tiers, i + 1);
              return (
                <th key={i} scope="col" className={TH}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${levelStyle(i + 1).dot}`} aria-hidden="true" />
                    Level {i + 1} Reward{' '}
                    {tier.scansRequired != null && (
                      <span className="text-[10px] lowercase font-normal opacity-75">({tier.scansRequired} scans)</span>
                    )}
                  </span>
                </th>
              );
            })}
            <th scope="col" className={`${TH} ${STICKY_ACTIONS} bg-surface-container-low text-right w-28`}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className={`divide-y divide-surface-container transition-opacity ${refreshing ? 'opacity-50' : ''}`}>
          {loading ? (
            <SkeletonRows columns={columns} />
          ) : (
            coupons.map((pkg) => {
              const updated = describeTime(pkg.updatedAt);
              const isActive = pkg.status === 'active';
              const by = pkg.updatedBy?.name;
              const items = [
                !isActive && { label: 'Make active', onSelect: () => onMakeActive(pkg) },
                { label: 'Duplicate', onSelect: () => onDuplicate(pkg) },
                {
                  label: 'Delete',
                  danger: true,
                  disabled: isActive,
                  hint: isActive ? ACTIVE_LOCK_HINT : undefined,
                  onSelect: () => onDelete(pkg),
                },
              ].filter(Boolean);

              return (
                <tr key={pkg.id} className={`group hover:bg-surface-container-low/60 transition-colors ${isActive ? 'bg-secondary-fixed/5' : ''}`}>
                  <th scope="row" className="py-3.5 px-4 align-top font-normal">
                    <div className="flex flex-col min-w-0">
                      <span
                        className={`text-[13px] font-semibold truncate max-w-[200px] ${pkg.name?.trim() ? 'text-on-surface' : 'italic text-on-surface-variant'}`}
                        title={packageTitle(pkg)}
                      >
                        {packageTitle(pkg)}
                      </span>
                      <span className="font-data-mono text-[11px] text-on-surface-variant">#{pkg.id}</span>
                    </div>
                  </th>
                  <td className="py-3.5 px-4 align-top">
                    <PackageStatusBadge status={pkg.status} />
                  </td>
                  <td className="py-3.5 px-4 align-top">
                    <div className="flex flex-col font-data-mono" title={by ? `by ${by}` : undefined}>
                      <span className="text-[11px] font-semibold text-on-surface whitespace-nowrap">{updated.exact}</span>
                      <span className="text-[11px] text-on-surface-variant">{updated.relative}</span>
                    </div>
                  </td>
                  {getLevels(pkg, tiers)
                    .slice(0, columns)
                    .map((level) => (
                      <LevelCell key={level.level} level={level} />
                    ))}
                  <td className={`py-3.5 px-4 align-top text-right ${STICKY_ACTIONS} bg-surface-container-lowest group-hover:bg-surface-container-low`}>
                    <div className="inline-flex items-center gap-0.5">
                      <IconButton icon="edit" label={`Edit coupon package #${pkg.id}`} onClick={() => onEdit(pkg)} />
                      <IconButton icon="visibility" label={`View coupon package #${pkg.id}`} onClick={() => onView(pkg)} />
                      <RowActionsMenu label={`More actions for coupon package #${pkg.id}`} items={items} />
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
